/**
 * Zusammenfassung (Schritt 7) — Texte der Zeilen wie im Design (`summary`, `settingDefs`), rein und testbar.
 * `t` ist die Übersetzungsfunktion; die Tests reichen eine Attrappe durch.
 */

import { autoReplySummary } from '../approach/auto-reply';
import {
  CLASS_KEYS,
  fmtNumber,
  isLinkedinUrl,
  judgingSkipped,
  searchLinkRows,
} from '../../campaign-wizard.logic';
import {
  effectiveRadius,
  isUnconfirmed,
  locationLabel,
  radiusLabel,
} from '../candidates/candidates-logic';
import { effectiveWorkWindow, WORK_TIME_ACCOUNT, workWindowLabel } from '../../work-window';
import {
  CriteriaSet,
  CriteriaTemplate,
  FlowPlanResult,
  RECONTACT_NEVER,
  RECONTACT_PRESETS,
  WizardAccount,
  WizardBlacklistList,
  WizardState,
  WizardStep,
} from '../../campaign-wizard.types';

export type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface SummaryRow {
  id:
    | 'job'
    | 'candidates'
    | 'blacklist'
    | 'judging'
    | 'list'
    | 'approach'
    | 'autoReply'
    | 'messages';
  labelKey: string;
  value: string;
  /** Ziel von „Bearbeiten“. */
  step: WizardStep;
}

/** Stand der Kandidatenvorschau für die Zusammenfassung. */
export interface SummaryCandidates {
  active: number;
  total: number;
  searchStatus?: 'none' | 'running' | 'finished' | 'failed';
  /** Kandidaten, die noch analysiert/bewertet werden. */
  analysisPending?: number;
}

export interface SummaryInput {
  state: WizardState;
  /** Geschätzte Suchtreffer; null = (noch) nicht bekannt. */
  estimate: number | null;
  lists: WizardBlacklistList[];
  templates: CriteriaTemplate[];
  candidates: SummaryCandidates | null;
  /** Kann das Absender-Konto einen Radius (Sales Navigator)? Ohne Angabe: nein. */
  radiusSupported?: boolean;
  /** Kann das Konto „PLZ + Umkreis“ (Sales Navigator oder Recruiter)? Ohne Angabe: nein. */
  postalSupported?: boolean;
  flowPlan: FlowPlanResult | null;
}

export const classesLabel = (classes: WizardState['classes'], t: Translate): string =>
  CLASS_KEYS.filter(key => classes[key]).join(' + ') || t('wizRevNoClass');

/** Weicht die Bewertung von der gewählten Vorlage ab? (Design: `modified`). */
export const isJudgingModified = (
  judging: CriteriaSet,
  templateId: string,
  templates: CriteriaTemplate[]
): boolean => {
  const template = templates.find(tp => tp.id === templateId);
  return template ? JSON.stringify(template.criteria) !== JSON.stringify(judging) : false;
};

const languageLabel = (language: WizardState['language'], t: Translate): string =>
  language === 'de'
    ? t('wizApprLangDe')
    : language === 'en'
      ? t('wizApprLangEn')
      : t('wizRevLangAuto');

/** „Deutsch + Englisch“ wie in Schritt 6: Primärsprache plus Sekundärsprache, falls gesetzt und verschieden. */
export const languagesLabel = (s: WizardState, t: Translate): string =>
  s.language2 && s.language2 !== s.language
    ? `${languageLabel(s.language, t)} + ${languageLabel(s.language2, t)}`
    : languageLabel(s.language, t);

/** Wurde der Suchstart abgelehnt (z. B. LinkedIn-Sitzung), obwohl der Server noch „nicht gestartet“ meldet: als fehlgeschlagen zeigen. */
export const withSearchFailure = (
  candidates: SummaryCandidates | null,
  failure: string | null
): SummaryCandidates | null => {
  if (!failure || !candidates) return candidates;
  return candidates.searchStatus === 'none' || candidates.searchStatus === undefined
    ? { ...candidates, searchStatus: 'failed' }
    : candidates;
};

/** Zusammenfassung der Ansprache (Design: `aSummary`). */
export const approachSummary = (s: WizardState, t: Translate): string =>
  [
    s.recruitsFor === 'own' ? t('wizApprRecruitsOwn') : t('wizRevRecruitsClients'),
    s.audience === 'new'
      ? t('wizApprAudienceNew')
      : s.audience === 'existing'
        ? t('wizApprAudienceExisting')
        : t('wizRevAudienceBoth'),
    ...(s.audience !== 'existing'
      ? [s.withNote ? t('wizRevNoteWith') : t('wizRevNoteWithout')]
      : []),
    s.formalSpeech === true ? t('wizRevFormalSie') : t('wizRevFormalDu'),
    ...(s.referral ? [t('wizRevReferral', { bonus: s.bonus || '' })] : []),
    languagesLabel(s, t),
  ].join(' · ');

/** Ein Ort der Zusammenfassung: Radius nur, wenn er wirklich gesucht wird; sonst „ganze Region“ bzw. „bitte bestätigen“. */
export const locationSummary = (
  location: WizardState['locations'][number],
  radiusSupported: boolean,
  t: Translate,
  postalSupported = false
): string => {
  if (isUnconfirmed(location)) return t('wizRevLocationUnconfirmed', { city: location.city });
  const radius = effectiveRadius(location, radiusSupported, postalSupported);
  // PLZ-Orte als „80331 + 25 km“; ohne Radius-Fähigkeit des Kontos wie ein Ort in der ganzen Region.
  if (location.postalCode && radius !== null)
    return `${locationLabel(location)} ${radiusLabel(radius)}`;
  return radius === null
    ? t('wizRevLocationWholeRegion', { city: location.city })
    : `${location.city} ${radiusLabel(radius)}`;
};

/** „Kandidatenliste“-Zeile je nach Suchstatus: „weitere laufend“ nur, wenn wirklich noch etwas läuft. */
export const candidateListSummary = (
  candidates: SummaryCandidates | null,
  t: Translate
): string => {
  if (!candidates) return t('wizRevListPending');
  const counts = { active: candidates.active, total: candidates.total };
  switch (candidates.searchStatus) {
    case 'running':
      return t('wizRevListValue', counts);
    case 'failed':
      return t('wizRevListFailed');
    case 'none':
      return t('wizRevListNotStarted');
    default:
      if (candidates.total === 0) return t('wizRevListNone');
      return (candidates.analysisPending ?? 0) > 0
        ? t('wizRevListValue', counts)
        : t('wizRevListDone', counts);
  }
};

export const buildSummaryRows = (input: SummaryInput, t: Translate): SummaryRow[] => {
  const {
    state,
    estimate,
    lists,
    templates,
    candidates,
    flowPlan,
    radiusSupported = false,
    postalSupported = false,
  } = input;

  const locations =
    state.locations.map(l => locationSummary(l, radiusSupported, t, postalSupported)).join(', ') ||
    t('wizRevNoLocation');
  const candidatesValue =
    state.method === 'saia'
      ? [
          t('wizRevSaiaSearch'),
          ...(estimate !== null ? [t('wizRevProfiles', { count: fmtNumber(estimate) })] : []),
          locations,
        ].join(' · ')
      : (() => {
          const links = searchLinkRows(state).filter(isLinkedinUrl).length;
          return links > 1 ? t('wizRevOwnSearches', { count: links }) : t('wizRevOwnSearch');
        })();

  const activeLists = lists
    .filter(l => state.globalBlacklists[l.id] ?? l.defaultOn)
    .map(l => l.name);
  const entryCount = state.blacklistCompanies.length + state.blacklistPeople.length;
  const entries = entryCount > 0 ? t('wizRevBlacklistEntries', { count: entryCount }) : '';
  // Ohne Liste und ohne Einträge: ausdrücklicher Hinweis statt einer leeren Zeile.
  const blacklistValue =
    [activeLists.join(', '), entries].filter(Boolean).join(' · ') || t('wizRevNoBlacklist');

  const modified = isJudgingModified(state.judging, state.templateId, templates);
  const judgingValue = judgingSkipped(state)
    ? t('wizRevJudgingSkipped')
    : [
        `${state.templateName}${state.templateName && modified ? ` ${t('wizRevModified')}` : ''}`,
        t('wizRevClass', { classes: classesLabel(state.classes, t) }),
      ]
        .filter(Boolean)
        .join(' · ');

  const listValue = candidateListSummary(candidates, t);

  // „Stelle“ = Jobtitel; der Kampagnenname steht separat dahinter, wenn er abweicht.
  const jobTitle = state.jobTitle.trim();
  const campaignName = state.name.trim();
  const jobValue = jobTitle
    ? campaignName && campaignName !== jobTitle
      ? `${jobTitle} · ${t('wizRevCampaignName', { name: campaignName })}`
      : jobTitle
    : campaignName;

  const messagesValue = flowPlan
    ? `${t('wizRevFlow', { count: flowPlan.msgCount })}${flowPlan.inmails > 0 ? t('wizRevFlowInmails', { count: flowPlan.inmails }) : ''} · ${t('wizRevFlowByReaction')}`
    : t('wizRevFlowPending');

  return [
    { id: 'job', labelKey: 'wizRevKeyJob', value: jobValue, step: 1 },
    { id: 'candidates', labelKey: 'wizRevKeyCandidates', value: candidatesValue, step: 2 },
    { id: 'blacklist', labelKey: 'wizRevKeyBlacklist', value: blacklistValue, step: 2 },
    { id: 'judging', labelKey: 'wizRevKeyJudging', value: judgingValue, step: 3 },
    { id: 'list', labelKey: 'wizRevKeyList', value: listValue, step: 4 },
    { id: 'approach', labelKey: 'wizRevKeyApproach', value: approachSummary(state, t), step: 5 },
    {
      id: 'autoReply',
      labelKey: 'wizRevKeyAutoReply',
      value: autoReplySummary(state.autoReply, t),
      step: 5,
    },
    { id: 'messages', labelKey: 'wizRevKeyMessages', value: messagesValue, step: 6 },
  ];
};

export type SettingField = 'account' | 'workTime' | 'approve' | 'approveContacts' | 'recontact';

export interface SettingRow {
  field: SettingField;
  icon: 'person' | 'schedule' | 'mark_email_read' | 'how_to_reg' | 'history';
  titleKey: string;
  value: string;
  options: { label: string; selected: boolean; patch: Partial<WizardState> }[];
  /** Pflichtauswahl noch nicht getroffen: die Zeile zeigt „Bitte auswählen“ und ist aufgeklappt. */
  needsChoice?: boolean;
  /** Erläuterung unter der Zeile. */
  hint?: string;
}

/** Schlüssel je Wert von `workTime` (Index = Wert). */
export const WORK_TIME_KEYS = [
  'wizRevWorkTime0',
  'wizRevWorkTime1',
  'wizRevWorkTime2',
  'wizRevWorkTime3',
] as const;
/** Reihenfolge der Auswahl: „Arbeitszeiten des Kontos“ (3) zuerst. */
export const WORK_TIME_ORDER = [3, 0, 1, 2] as const;
export const APPROVE_KEYS = ['wizRevApprove0', 'wizRevApprove1'] as const;
/** Hinweis unter der Freigabe-Zeile je gewählter Option. */
const APPROVE_HINT_KEYS = ['wizRevApproveHint0', 'wizRevApproveHint1'] as const;
export const APPROVE_CONTACTS_KEYS = ['wizRevApproveContacts0', 'wizRevApproveContacts1'] as const;
/** Übersetzungsschlüssel der Wiederansprache; 0 = keine Einschränkung, -1 = nie erneut, sonst Tage (alte Auswahl 14/28/60/90/180). */
const RECONTACT_KEYS: Record<number, string> = {
  0: 'wizRevRecontactNone',
  14: 'wizRevRecontact14',
  28: 'wizRevRecontact28',
  60: 'wizRevRecontact60',
  90: 'wizRevRecontact90',
  180: 'wizRevRecontact180',
  [RECONTACT_NEVER]: 'wizRevRecontactNever',
};

export const recontactLabel = (days: number, t: Translate): string =>
  RECONTACT_KEYS[days] ? t(RECONTACT_KEYS[days]) : t('wizRevRecontactDays', { days });

/** Die Auswahl: alte Fristen, „keine Einschränkung“, „nie erneut“ – und der aktuelle Wert, falls er (z. B. aus der Mandanten-Vorgabe) nicht dazugehört. */
export const recontactChoices = (current: number): number[] => {
  const presets = [...RECONTACT_PRESETS] as number[];
  if (presets.includes(current)) return presets;
  const days = presets.filter(d => d >= 0);
  const never = presets.filter(d => d < 0);
  return [...days, current, ...never].sort((a, b) => (a < 0 ? 1 : b < 0 ? -1 : a - b));
};

/** Die Einstellungszeilen von „So arbeitet der Agent“ (Design: `settingDefs`, dazu die Kontaktfreigabe). */
export const buildSettingRows = (
  state: WizardState,
  accounts: WizardAccount[],
  t: Translate
): SettingRow[] => {
  const accountOptions = accounts.map(a => ({
    label: a.name || a.email,
    selected: state.accountId === a.id,
    patch: { accountId: a.id } as Partial<WizardState>,
  }));
  const current = (opts: { label: string; selected: boolean }[]) =>
    opts.find(o => o.selected)?.label ?? '';
  // Arbeitszeiten: was wirklich gilt (Kontofenster ∩ Auswahl, bei „wie im Konto“ das Kontofenster); ohne Kontofenster nur die Auswahl.
  const account = accounts.find(a => a.id === state.accountId);
  const workTime = WORK_TIME_ORDER.map(value => {
    const window = effectiveWorkWindow(account, value);
    const label = window
      ? value === WORK_TIME_ACCOUNT
        ? `${workWindowLabel(window, t)} ${t('wizRevWorkTimeAccountSuffix')}`
        : workWindowLabel(window, t)
      : t(WORK_TIME_KEYS[value]);
    return {
      label,
      selected: state.workTime === value,
      patch: { workTime: value } as Partial<WizardState>,
    };
  });
  // Freigabe und „Bereits Kontaktierte“ sind Pflichtauswahl: erst nach dem Klick gilt eine Option als gewählt.
  const approveChosen = state.approveChosen === true;
  const recontactChosen = state.recontactChosen === true;
  const approve = APPROVE_KEYS.map((key, i) => ({
    label: t(key),
    selected: approveChosen && state.approve === i,
    patch: { approve: i, approveChosen: true } as Partial<WizardState>,
  }));
  const approveContacts = APPROVE_CONTACTS_KEYS.map((key, i) => ({
    label: t(key),
    selected: (state.approveContacts ?? 1) === i,
    patch: { approveContacts: i } as Partial<WizardState>,
  }));
  const recontact = recontactChoices(state.recontactDays).map(days => ({
    label: recontactLabel(days, t),
    selected: recontactChosen && state.recontactDays === days,
    patch: { recontactDays: days, recontactChosen: true } as Partial<WizardState>,
  }));
  return [
    {
      field: 'account',
      icon: 'person',
      titleKey: 'wizRevSettingAccount',
      value: current(accountOptions),
      options: accountOptions,
    },
    {
      field: 'workTime',
      icon: 'schedule',
      titleKey: 'wizRevSettingWorkTime',
      value: current(workTime),
      options: workTime,
    },
    {
      field: 'approve',
      icon: 'mark_email_read',
      titleKey: 'wizRevSettingApprove',
      value: approveChosen ? current(approve) : t('wizRevChoose'),
      options: approve,
      needsChoice: !approveChosen,
      hint: approveChosen ? t(APPROVE_HINT_KEYS[state.approve]) : undefined,
    },
    {
      field: 'approveContacts',
      icon: 'how_to_reg',
      titleKey: 'wizRevSettingApproveContacts',
      value: current(approveContacts),
      options: approveContacts,
    },
    {
      field: 'recontact',
      icon: 'history',
      titleKey: 'wizRevSettingRecontact',
      value: recontactChosen ? current(recontact) : t('wizRevChoose'),
      options: recontact,
      needsChoice: !recontactChosen,
      hint: t('wizRevRecontactHint'),
    },
  ];
};
